import { DataSharingQuery } from 'dashboard-services/api/dataSharing';
import { DataStorageEnums } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { globalActions } from 'primary-components';

import getApiConfig from 'api/ApiConfig';

import AuthUtils from './AuthUtils';
import ExcelUtils from './ExcelUtils';

import dayjs from 'dayjs';

export default (() => {
  const EXCEL_EPOCH_START = "1899-12-30"
  const MAX_PARAM_LENGTH = 255
  const MAX_FORMULA_LENGTH = 8_192
  const NG_FORMULA_PREFIX = "=NG."
  const OVERRIDDEN = "Overridden"

  const addAndValidateParam = ({ paramName, paramValue, paramsExceedingLength, withComma = true, rawParam = undefined }) => {
    if(rawParam) {
      return (withComma ? ", " : "") + rawParam
    }
    const paramContent = `${paramName}=${paramValue}`
    if(paramContent.length > MAX_PARAM_LENGTH) {
      const id = CommonService.generateUID()
      paramsExceedingLength.push({ name: paramName, value: paramContent.replace(/""/g, '"'), id })
      return (withComma ? ", " : "") + id
    }
    return (withComma ? ", " : "") + `"${paramContent}"`
  }

  const formulaValidator = async ({ formula, paramsExceedingLength, address, setShowTooLongFormula, setAddress }) => {
    if(formula.length > MAX_FORMULA_LENGTH) {
      setShowTooLongFormula(true)
      CommonService.copyToClipboard(formula)
    } else {
      setShowTooLongFormula(false)
      const addressLocal = await ExcelUtils.addToFormulaCell({ formula, address, paramsExceedingLength })
      if(addressLocal) {
        setAddress(addressLocal)
      }
    }
  }

  const validateProviders = ({ keys, onError }) => {
    let uniqueProviders = []
    if(keys.length > 0) {
      uniqueProviders = keys.map(key => key?.source).toUnique()
      if(uniqueProviders?.length > 1) {
        onError?.()
      }
    }
    return uniqueProviders;
  }

  const showFormulaWarning = ({ address, message }) => dispatch => {
    setTimeout(() => {
      dispatch(globalActions.getToastsStore()).warning(message, "Delete Formula", {
        withCancel: false,
        confirmText: "Delete Formula",
        confirmAction: () => {
          ExcelUtils.clearTableWithFormulaAddress(address)
        }
      })
    }, 5)   
  }

  const validateProvidersForEdit = ({ keys, address }) => async dispatch => {
    const uniqueProviders = validateProviders({ keys })
    if(uniqueProviders.length > 1) {
      dispatch(showFormulaWarning({ address, message: "Formula has been modified and cannot be restored or edited." }))
      console.warn("Formula has been modified and cannot be restored or edited.")
      return { shouldContinue: false };
    }
    await dispatch(AuthUtils.validateToken())
    const getQueryBuilder = () => new Query.Builder(dispatch(getApiConfig()))
            .withFetching(true),
          { queryFn } = DataSharingQuery.QUERIES.getProducers(getQueryBuilder, {
            includeSelfProducer: true,
          })
    console.info("Starting provider fetching...")
    const providers = await queryFn({ pageParam: 0 }) 
    const producers = providers?.producers || []
    console.info("Fetched providers:", providers)
    if(uniqueProviders[0] !== undefined && producers.findIndex(provider => provider?.name === uniqueProviders[0]) === -1) {
      console.warn(`You don't have access to ${uniqueProviders[0]} source. Formula cannot be edited.`)
      dispatch(showFormulaWarning({ address, message: `You don't have access to ${uniqueProviders[0]} source. Formula cannot be edited.` }))
      return { shouldContinue: false };
    }
    const defaultSource = producers.find(source => source.producerId === 0)?.name
    console.info("Default source:", defaultSource)
    return { defaultSource, uniqueProviders, shouldContinue: true }
  }

  const prepColumnHeaders = ({ csvHeaders, formatType }) => {
    if(formatType === DataStorageEnums.DATA_FORMAT.NCSV) {
      return ""
    }
    return csvHeaders?.map(header => `{${Object.entries(header).filter(([, value]) => value !== undefined).map(([key, value]) => `""${key}"":""${value}""`).join(", ")}}`).join(", ")
  }

  const getAllNGFormulas = async ({ context }) => {
    const workbook = context.workbook
    const worksheets = workbook.worksheets
    worksheets.load("items/name")
    await context.sync()

    const results = []
    for (const sheet of worksheets.items) {
      const usedRange = sheet.getUsedRange(true)
      usedRange.load(["formulas", "rowIndex", "columnIndex", "rowCount", "columnCount"])
      await context.sync()
      const formulas = usedRange.formulas
      const rowOffset = usedRange.rowIndex
      const colOffset = usedRange.columnIndex

      for (let r = 0; r < formulas.length; r++) {
        for (let c = 0; c < formulas[r].length; c++) {
          const value = formulas[r][c]
          if (typeof value === "string" && value.startsWith(NG_FORMULA_PREFIX)) {
            const cell = sheet.getCell(rowOffset + r, colOffset + c)
            cell.load("address")

            results.push({
              sheetName: sheet.name,
              cell,
              formula: value
            })
          }
        }
      }
    }
    await context.sync()

    return results.map(r => ({
      sheetName: r.sheetName,
      address: r.cell.address,
      formula: r.formula
    }))
  }

  const stringifyExplicitSymbolsForFormula = (symbols = {}) => (
    `{${Object.entries(symbols)
      .map(([k, v]) => `""${k}"": ""${v}""`)
      .join(", ")}}`
  )

  const isLikelyExcelDateSerial = value => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return false;
    }

    // Excel date serials are positive day counts, optionally with a fractional time part.
    if (value < 1) {
      return false;
    }

    // 2958465 is Excel's max date: 9999-12-31 in the 1900 system.
    if (value > 2958465) {
      return false;
    }

    return true;
  }

  const isRawFormulaOverridden = rawParam => typeof rawParam === "string" && rawParam.includes("&")

  const excelSerialToDateString = ({ value, withTime = false, isEndDate = false } = {}) => {
    const excelEpoch = dayjs.utc(EXCEL_EPOCH_START)
    const wholeDays = Math.floor(value)
    const fractionDay = value - wholeDays
    const date = excelEpoch.add(wholeDays, "day").add(fractionDay * 86400000, "millisecond")

    if(!withTime) {
      return date.format(TimeService.ISO_DATE_FORMAT)
    }

    return `${date.format(TimeService.ISO_DATE_FORMAT)}T${isEndDate ? "23:59:59.999" : "00:00:00.000"}`
  }

  return {
    formulaValidator,
    addAndValidateParam,
    prepColumnHeaders,
    validateProviders,
    validateProvidersForEdit,
    getAllNGFormulas,
    excelSerialToDateString,
    isRawFormulaOverridden,
    isLikelyExcelDateSerial,
    stringifyExplicitSymbolsForFormula,
    MAX_PARAM_LENGTH,
    MAX_FORMULA_LENGTH,
    NG_FORMULA_PREFIX,
    OVERRIDDEN
  }
})()
